import { keyframes } from '@emotion/react'
import { Box, Stack, Typography } from '@mui/material'
import BlueLogo from 'shared/components/BlueLogo'

interface DownloadPageProps {
    link: string | null
    filename: string | null
    error: string | null
    countdown: number | null
    onDownload: () => void
}

const pulse = keyframes`
    0%, 100% { opacity: 0.4; transform: scale(0.8); }
    50% { opacity: 1; transform: scale(1); }
`

function extractDisplayName(path: string | null): string | null {
    if (!path) return null
    const parts = path.split('/')
    return parts[parts.length - 1] || null
}

export function DownloadPage({
    link,
    filename,
    error,
    countdown,
    onDownload,
}: DownloadPageProps) {
    const displayName = extractDisplayName(filename)
    const isLoading = !link && !error
    const isReady = !!link
    const isError = !!error

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(180deg, #ffffff 0%, #f1f7ff 100%)',
            }}
        >
            <Stack
                spacing={6}
                alignItems="center"
                sx={{ maxWidth: 400, px: 3 }}
            >
                <BlueLogo widthOverride="180px" />

                {isLoading && (
                    <Stack spacing={4} alignItems="center">
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {[0, 1, 2].map((i) => (
                                <Box
                                    key={i}
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        backgroundColor: '#006CB5',
                                        animation: `${pulse} 1.4s ease-in-out infinite`,
                                        animationDelay: `${i * 0.2}s`,
                                    }}
                                />
                            ))}
                        </Box>
                        <Typography
                            sx={{
                                color: '#64748b',
                                fontSize: '0.95rem',
                                fontWeight: 400,
                                letterSpacing: '0.01em',
                            }}
                        >
                            Preparing your download...
                        </Typography>
                    </Stack>
                )}

                {isReady && (
                    <Stack spacing={2} alignItems="center">
                        {displayName && (
                            <Typography
                                sx={{
                                    color: '#334155',
                                    fontSize: '0.95rem',
                                    fontFamily: 'monospace',
                                }}
                            >
                                {displayName}
                            </Typography>
                        )}
                        <Typography
                            sx={{
                                color: '#64748b',
                                fontSize: '0.9rem',
                                textAlign: 'center',
                                lineHeight: 1.6,
                            }}
                        >
                            {countdown !== null && countdown > 0
                                ? `Starting in ${countdown} second${countdown !== 1 ? 's' : ''}...`
                                : 'Starting download...'}
                            <br />
                            {"If your download doesn't start, "}
                            <Box
                                component="span"
                                onClick={onDownload}
                                sx={{
                                    color: '#006CB5',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        textDecoration: 'underline',
                                    },
                                }}
                            >
                                click here
                            </Box>
                            .
                        </Typography>
                    </Stack>
                )}

                {isError && (
                    <Stack spacing={2} alignItems="center">
                        <Typography
                            sx={{
                                color: '#334155',
                                fontSize: '1.1rem',
                                fontWeight: 500,
                            }}
                        >
                            Download unavailable
                        </Typography>
                        <Typography
                            sx={{
                                color: '#94a3b8',
                                fontSize: '0.9rem',
                                textAlign: 'center',
                                maxWidth: 280,
                                lineHeight: 1.5,
                            }}
                        >
                            {error}
                        </Typography>
                    </Stack>
                )}
            </Stack>
        </Box>
    )
}
